import { lazy, Suspense } from "react"
import { Navigate, RouteObject } from "react-router-dom"

import ErrorMessage from "@/components/shared/ErrorMessage.tsx"
import Spinner from "@/components/ui/Spinner.tsx"
import { PaymentTelemetryBoundary } from "@/features/payment/PaymentTelemetryBoundary.tsx"
import DashboardLayout from "@/pages/dashboard/layout-protected.tsx"

import { ReportsComingSoon } from "./comingSoonRoutes/ReportsComingSoon"
import { Scope1Blocked } from "./comingSoonRoutes/Scope1Blocked"
import AuthRoute from "./AuthRoute.tsx"
import { BookingIndexRedirect } from "./BookingIndexRedirect.tsx"
import { LegacyBookingRedirect } from "./LegacyBookingRedirect.tsx"
import ProtectedRoute from "./ProtectedRoute.tsx"

// --- Page Components (Lazy Loaded) ---
const LoginPage = lazy(() => import("@/pages/auth/login/page-auth.tsx"))
const RegisterPage = lazy(() => import("@/pages/auth/register/page-auth.tsx"))
const AuthenticatorSetupPage = lazy(() => import("@/pages/auth/authenticator-setup/page.tsx"))
const CompanyDetailsPage = lazy(() => import("@/pages/company-details/page.tsx"))
const MunicipalityDetailsPage = lazy(() => import("@/pages/municipality-details/page.tsx"))
const CompanyFormPage = lazy(() => import("@/pages/company-form/page.tsx"))
const CompanyLocationFormPage = lazy(() => import("@/pages/company-location-form/page.tsx"))
const CompanyRelatedEntitiesFormPage = lazy(() => import("@/pages/company-related-entities-form/page.tsx"))
const DashboardCompanyLocationsPage = lazy(() => import("@/pages/dashboard/company/locations/page.tsx"))
const DashboardCompanyPage = lazy(() => import("@/pages/dashboard/company/page.tsx"))
const DashboardCompanyRelatedEntitiesPage = lazy(
  () => import("@/pages/company-related-entities-form/components/page.tsx"),
)
const DashboardReportingYearPage = lazy(() => import("@/pages/dashboard/reporting-year/page.tsx"))
const DashboardImpactPage = lazy(() => import("@/pages/dashboard/impact/page.tsx"))
const DashboardWalletPage = lazy(() => import("@/pages/dashboard/wallet/page.tsx"))
const DashboardCampaignManagerPage = lazy(() => import("@/pages/dashboard/campaign-manager/page.tsx"))
const BookingLayout = lazy(() => import("@/pages/dashboard/campaign-manager/booking/layout.tsx"))
const BookingSelectPage = lazy(() => import("@/pages/dashboard/campaign-manager/booking/select/page.tsx"))
const BookingContentPage = lazy(() => import("@/pages/dashboard/campaign-manager/booking/content/page.tsx"))
const BookingReviewPage = lazy(() => import("@/pages/dashboard/campaign-manager/booking/review/page.tsx"))
const DashboardCampaignOverviewPage = lazy(
  () => import("@/pages/dashboard/campaign-manager/campaign-overview/page.tsx"),
)
const BookingPaymentPage = lazy(() => import("@/pages/dashboard/campaign-manager/booking/payment/page.tsx"))
const BookingPaymentStatusPage = lazy(
  () => import("@/pages/dashboard/campaign-manager/booking/payment/status/page.tsx"),
)
const ConfirmPaymentPage = lazy(() => import("@/pages/dashboard/campaign-manager/confirm-payment/page.tsx"))
const DashboardCampaignsPage = lazy(() => import("@/pages/dashboard/campaigns/page.tsx"))

const DashboardEntry = lazy(() => import("@/pages/dashboard/entry.tsx"))
const DashboardPage = lazy(() => import("@/pages/dashboard/page.tsx"))
const DashboardNotificationSettingsPage = lazy(() => import("@/pages/dashboard/settings/notifications/page.tsx"))
const DashboardSecuritySettingsPage = lazy(() => import("@/pages/dashboard/settings/security/page.tsx"))
const DashboardNotificationsPage = lazy(() => import("@/pages/dashboard/notifications/page.tsx"))
const DashboardResultsPage = lazy(() => import("@/pages/dashboard/results/page.tsx"))
const DashboardAccountingPage = lazy(() => import("@/pages/dashboard/accounting/page.tsx"))
const DashboardScope1FugitiveCombustionPage = lazy(
  () => import("@/pages/dashboard/scope-1/fugitive-combustion/page.tsx"),
)
const DashboardScope1MobileCombustionPage = lazy(() => import("@/pages/dashboard/scope-1/mobile-combustion/page.tsx"))
const DashboardScope1Page = lazy(() => import("@/pages/dashboard/scope-1/page.tsx"))
const DashboardScope1StationaryCombustionPage = lazy(
  () => import("@/pages/dashboard/scope-1/stationary-combustion/page.tsx"),
)
const DashboardScope2Page = lazy(() => import("@/pages/dashboard/scope-2/energy/page.tsx"))
const DashboardScope2EacPage = lazy(() => import("@/pages/dashboard/scope-2/eac/page.tsx"))
const DashboardScope3Page = lazy(() => import("@/pages/dashboard/scope-3/page.tsx"))
const DashboardMupiContentStudioPage = lazy(() => import("@/pages/dashboard/content-studio/page.tsx"))
const EACFormPage = lazy(() => import("@/pages/add-eac/page.tsx"))
const NotFoundPage = lazy(() => import("@/pages/not-found/page.tsx"))
const Page = lazy(() => import("@/pages/page.tsx"))
const StationaryCombustionPage = lazy(() => import("@/pages/dashboard/scope-1/stationary-combustion/page.tsx"))

// --- Loading Fallback ---
const LoadingFallback = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <Spinner large className="h-24 w-24" />
  </div>
)

/**
 * - Wraps each element in a <Suspense> boundary for lazy loading
 * - Adds a default errorElement to each route
 */
const processRoutes = (routes: RouteObject[]): RouteObject[] => {
  return routes.map((route) => {
    const processedRoute = { ...route }

    // Wrap the element in a Suspense boundary if it exists
    if (processedRoute.element) {
      processedRoute.element = <Suspense fallback={<LoadingFallback />}>{processedRoute.element}</Suspense>
    }

    // Add a default error element to every route
    processedRoute.errorElement = <ErrorMessage />

    // Recursively process any children
    if (processedRoute.children) {
      processedRoute.children = processRoutes(processedRoute.children)
    }

    return processedRoute
  })
}

const rawRoutes: RouteObject[] = [
  {
    path: "",
    children: [
      { index: true, element: <Page /> },
      {
        path: "login",
        element: (
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        ),
      },
      {
        path: "register",
        element: (
          <AuthRoute>
            <RegisterPage />
          </AuthRoute>
        ),
      },
      { path: "store" }, // Placeholder route
      {
        path: "authenticator-setup",
        element: (
          <ProtectedRoute>
            <AuthenticatorSetupPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "company-details",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <CompanyDetailsPage />
              </ProtectedRoute>
            ),
          },
          { path: "store" },
        ],
      },
      {
        path: "municipality-details",
        children: [{ index: true, element: <MunicipalityDetailsPage /> }],
      },
      {
        path: "company-form",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <CompanyFormPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "company-location-form",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <CompanyLocationFormPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "company-related-entities-form",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <CompanyRelatedEntitiesFormPage />
              </ProtectedRoute>
            ),
          },
          { path: "steps" },
        ],
      },
      {
        path: "dashboard",
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <DashboardEntry />,
          },
          { path: "climate", element: <DashboardPage /> },
          { path: "climate/setup", element: <Scope1Blocked /> },
          {
            path: "company",
            children: [
              {
                index: true,
                element: <DashboardCompanyPage />,
              },
              {
                path: "locations",
                element: <DashboardCompanyLocationsPage />,
              },
              {
                path: "reporting-year",
                element: (
                  <ProtectedRoute>
                    <DashboardReportingYearPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: "related-entities",
                children: [
                  {
                    index: true,
                    element: <DashboardCompanyRelatedEntitiesPage />,
                  },
                  { path: "forms" },
                ],
              },
            ],
          },
          {
            path: "impact",
            element: (
              <ProtectedRoute>
                <DashboardImpactPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "wallet",
            element: (
              <ProtectedRoute>
                <DashboardWalletPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "content-studio",
            element: (
              <ProtectedRoute>
                <DashboardMupiContentStudioPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "campaign-manager",
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute>
                    <DashboardCampaignManagerPage />
                  </ProtectedRoute>
                ),
              },
              {
                // The retired v1 booking wizard: old links open the equivalent booking step.
                path: "mupi-selection",
                element: <LegacyBookingRedirect />,
              },
              {
                path: "booking",
                element: (
                  <ProtectedRoute>
                    <BookingLayout />
                  </ProtectedRoute>
                ),
                children: [
                  { index: true, element: <BookingIndexRedirect /> },
                  { path: "select", element: <BookingSelectPage /> },
                  { path: "content", element: <BookingContentPage /> },
                  { path: "review", element: <BookingReviewPage /> },
                  {
                    path: "payment",
                    element: (
                      <PaymentTelemetryBoundary>
                        <BookingPaymentPage />
                      </PaymentTelemetryBoundary>
                    ),
                  },
                  {
                    path: "payment/status",
                    element: (
                      <PaymentTelemetryBoundary>
                        <BookingPaymentStatusPage />
                      </PaymentTelemetryBoundary>
                    ),
                  },
                ],
              },
              {
                path: "campaign-overview",
                element: (
                  <ProtectedRoute>
                    <DashboardCampaignOverviewPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: "campaign-payment",
                element: (
                  <ProtectedRoute>
                    <PaymentTelemetryBoundary>
                      <BookingPaymentPage />
                    </PaymentTelemetryBoundary>
                  </ProtectedRoute>
                ),
              },
              {
                path: "confirm-payment",
                element: (
                  <ProtectedRoute>
                    <PaymentTelemetryBoundary>
                      <ConfirmPaymentPage />
                    </PaymentTelemetryBoundary>
                  </ProtectedRoute>
                ),
              },
              {
                path: "campaign-payment/status",
                element: (
                  <ProtectedRoute>
                    <PaymentTelemetryBoundary>
                      <BookingPaymentStatusPage />
                    </PaymentTelemetryBoundary>
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            // Sibling of campaign-manager, not a child: campaign-manager is the search and booking
            // funnel, this is the campaign list the sidebar points at.
            path: "campaigns",
            element: (
              <ProtectedRoute>
                <DashboardCampaignsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "notifications",
            element: (
              <ProtectedRoute>
                <DashboardNotificationsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "settings/notifications",
            element: (
              <ProtectedRoute>
                <DashboardNotificationSettingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "settings/security",
            element: (
              <ProtectedRoute>
                <DashboardSecuritySettingsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "results",
            element: (
              <ProtectedRoute>
                <DashboardResultsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "accounting",
            element: (
              <ProtectedRoute>
                <DashboardAccountingPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "reports",
            element: (
              <ProtectedRoute>
                <ReportsComingSoon />
              </ProtectedRoute>
            ),
          },
          {
            path: "scope-1",
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute>
                    <DashboardScope1Page />
                  </ProtectedRoute>
                ),
              },
              {
                path: "fugitive-combustion",
                element: (
                  <ProtectedRoute>
                    <DashboardScope1FugitiveCombustionPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: "mobile-combustion",
                element: (
                  <ProtectedRoute>
                    <DashboardScope1MobileCombustionPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: "stationary-combustion",
                element: (
                  <ProtectedRoute>
                    <DashboardScope1StationaryCombustionPage />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: "scope-2/energy",
            element: (
              <ProtectedRoute>
                <DashboardScope2Page />
              </ProtectedRoute>
            ),
          },
          {
            path: "scope-2/eac",
            element: (
              <ProtectedRoute>
                <DashboardScope2EacPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "scope-3",
            element: (
              <ProtectedRoute>
                <DashboardScope3Page />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "add-eac",
        element: (
          <ProtectedRoute>
            <EACFormPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "stationary-combustion",
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <StationaryCombustionPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      { path: "not-found", element: <NotFoundPage /> },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/not-found" replace />,
  },
]

// Process the raw routes to add the shared configuration
const routerRoutes = processRoutes(rawRoutes)

export default routerRoutes
